<script setup lang="ts">
import { errorString } from '@/utils/helpers/rest-response';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useRouter } from '@/router/router';
import { store as vuex } from '@/store/store';
import { computed, shallowRef } from 'vue';
import { isEmail, required, useRules } from '@/composables/vuetify-validation';

const router = useRouter();
const snackbarManager = useSnackbarManager();

const props = withDefaults(
  defineProps<{
    lostEmail?: string;
    lostMessage?: string;
  }>(),
  { lostEmail: '', lostMessage: '' }
);

const isProcessing = shallowRef(false);
const emailAddress = shallowRef(props.lostEmail);
const tfaError = shallowRef('');

const clientConfig = computed(() => vuex.state.clientConfig!);

function resetMessage(message: string, actionText: string) {
  snackbarManager.show({ message: message, actionText: actionText });
}

const { rules, validateAndSubmit } = useRules<{ emailAddress: string }>({
  emailAddress: {
    rules: [
      (v) => required(v, `${i18n.t('emailAddress')} ${i18n.t('formfield.IsRequired')}`),
      (v) => !v || isEmail(v, i18n.t('formfield.InvalidEmail').toString()),
    ],
  },
});

const onSubmit = () =>
  validateAndSubmit(async () => {
    try {
      if (isProcessing.value) return;
      isProcessing.value = true;

      tfaError.value = '';

      await vuex.dispatch('lost2FADevice', emailAddress.value);

      resetMessage(
        i18n.t('loginResetDeviceEmailSent', {
          emailAddress: emailAddress.value,
        }) as string,
        i18n.t('loginClose') as string
      );
      await router.push({
        name: 'reset-2fa',
        params: {
          loginEmail: emailAddress.value,
          lostMessage: 'loginResetDeviceEmailSent',
        },
      });
    } catch (e) {
      tfaError.value = errorString(e);
    } finally {
      isProcessing.value = false;
    }
  });
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="600"
    origin="center center"
    overlay-color="secondary"
    overlay-opacity="0.25"
    persistent
    transition="scale-transition"
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t('loginLostDeviceTitle') }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer class="mt-6">
            <VAlert v-if="tfaError" type="error">
              {{ tfaError }}
              <br />
              {{ $t('loginLostDeviceError2') }}
            </VAlert>
            <VAlert v-else border="left" color="primary" icon="mdi-information" text>
              {{ $t('loginLostDeviceInfo1', { title: clientConfig?.systemTitleShort }) }}
              <br />
              {{ $t('loginLostDeviceInfo2') }}
              <br />
              {{ $t('loginLostDeviceInfo3') }}
            </VAlert>
          </VContainer>

          <VContainer class="mt-6">
            <VTextField
              v-model="emailAddress"
              autocomplete="username email"
              autofocus
              :label="$t('loginEmail')"
              required
              v-bind="rules.emailAddress"
            >
            </VTextField>
          </VContainer>
        </VCardText>
        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text to="/">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            class="px-8 mx-4"
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            type="submit"
            @click="onSubmit"
          >
            <VIcon left>mdi-email</VIcon>
            {{ $t('loginResetButton') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
